import React from 'react';
import { FileText, Package, CheckCircle, Clock, Truck, Paperclip, ClipboardCheck, ShieldCheck, ClipboardList, Warehouse } from 'lucide-react';

const TIMELINE_STAGES = [
  { id: 'order_created', label: 'Order Created', icon: <FileText size={18} /> },
  { id: 'moc_created', label: 'MOC Created', icon: <Package size={18} /> },
  { id: 'moc_approved_finance', label: 'MOC Approved', icon: <ShieldCheck size={18} /> },
  { id: 'packing_scheduled', label: 'Packing Scheduled', icon: <Clock size={18} /> },
  { id: 'packed_ready', label: 'Packing Done', icon: <ClipboardCheck size={18} /> },
  { id: 'dc_created', label: 'DC Completed', icon: <FileText size={18} /> },
  { id: 'documents_ready', label: 'Documents Ready', icon: <Paperclip size={18} /> },
  { id: 'qc_dispatched', label: 'QC & Dispatched', icon: <Truck size={18} /> },
  { id: 'delivered', label: 'Delivered', icon: <CheckCircle size={18} /> },
];

const INTERBRANCH_TIMELINE_STAGES = [
  { id: 'moc_created', label: 'MOC Created', icon: <Package size={18} /> },
  { id: 'packing_scheduled', label: 'Packing Scheduled', icon: <Clock size={18} /> },
  { id: 'packed_ready', label: 'Packing Done', icon: <ClipboardCheck size={18} /> },
  { id: 'dc_created', label: 'DC Completed', icon: <FileText size={18} /> },
  { id: 'documents_ready', label: 'Documents Ready', icon: <Paperclip size={18} /> },
  { id: 'qc_dispatched', label: 'QC & Dispatched', icon: <Truck size={18} /> },
  { id: 'delivered', label: 'Delivered', icon: <CheckCircle size={18} /> },
];

const getTimelineStageFromStatus = (status, isInterbranchTransfer = false) => {
  switch (status) {
    case 'draft':
    case 'confirmed_order':
    case 'in_que':
    case 'ops_rejected':
    case 'ops_hold':
      return 'order_created';
    case 'moc_draft':
    case 'moc_in_progress':
      return 'moc_created';
    case 'moc_pending_finance':
    case 'fin_hold':
    case 'fin_rejected':
      return 'moc_approved_finance';
    case 'moc_rejected':
      return 'moc_created';
    case 'moc_approved':
    case 'packing_queued':
      return isInterbranchTransfer ? 'moc_created' : 'moc_approved_finance';
    case 'packing_scheduled':
    case 'packing_hold':
    case 'packing_rejected':
      return 'packing_scheduled';
    case 'packing': 
    case 'packed_ready':
      return 'packed_ready';
    case 'invoice_pending':
    case 'ewaybill_pending':
    case 'dc_hold':
      return 'dc_created';
    case 'invoice_ready':
    case 'ewaybill_complete':
    case 'dc_confirmed':
      return 'documents_ready';
    case 'qc_passed':
    case 'dispatched':
      return 'qc_dispatched';
    case 'in_transit':
    case 'delivered':
      return 'delivered';
    default:
      return isInterbranchTransfer ? 'moc_created' : 'order_created';
  }
};

const HorizontalTimeline = ({ orderData, dispatchesData, dispatchData, title }) => {
  const primaryDispatch = dispatchData || dispatchesData?.[0] || null;
  const isInterbranchTransfer =
    dispatchData?.typeOfMOC === 'Interbranch Transfer' ||
    dispatchesData?.some(dispatch => dispatch.typeOfMOC === 'Interbranch Transfer') ||
    primaryDispatch?.typeOfMOC === 'Interbranch Transfer';

  if (!orderData && !dispatchData && (!dispatchesData || dispatchesData.length === 0)) return null;

  const timelineStages = isInterbranchTransfer ? INTERBRANCH_TIMELINE_STAGES : TIMELINE_STAGES;
  const defaultStageId = isInterbranchTransfer ? 'moc_created' : 'order_created';

  let currentOverallStageId = defaultStageId;
  
  if (dispatchData) {
    // Specific MOC Timeline
    currentOverallStageId = getTimelineStageFromStatus(dispatchData.dispatchStatus, isInterbranchTransfer);
  } else if (dispatchesData && dispatchesData.length > 0) {
    // Aggregate Timeline (Master)
    let furthestIndex = -1;
    for (const dispatch of dispatchesData) {
      const stageId = getTimelineStageFromStatus(dispatch.dispatchStatus, dispatch.typeOfMOC === 'Interbranch Transfer');
      const stageIndex = timelineStages.findIndex(s => s.id === stageId);
      if (stageIndex > furthestIndex) {
        furthestIndex = stageIndex;
        currentOverallStageId = stageId;
      }
    }
  } else {
    // Base Order Timeline (No MOCs yet)
    currentOverallStageId = isInterbranchTransfer ? defaultStageId : getTimelineStageFromStatus(orderData.status, false);
  }

  let currentStageIndex = timelineStages.findIndex(s => s.id === currentOverallStageId);
  if (currentStageIndex === -1) currentStageIndex = 0;

  // If the status is truly 'delivered' (not just 'in_transit'), we mark the 'delivered' 
  // stage as completed (green) instead of active (blue pulsing).
  if (currentOverallStageId === 'delivered') {
    if (dispatchData && dispatchData.dispatchStatus === 'delivered') {
      currentStageIndex = timelineStages.length;
    } else if (dispatchesData && dispatchesData.length > 0) {
      if (dispatchesData.every(d => d.dispatchStatus === 'delivered')) {
        currentStageIndex = timelineStages.length;
      }
    } else if (orderData?.status === 'delivered') {
      currentStageIndex = timelineStages.length;
    }
  }

  const getTimestampForStage = (stageId) => {
    if (stageId === 'order_created') {
      return orderData?.createdAt || null;
    }

    // If we are looking at a specific MOC
    if (dispatchData) {
        let matchedTimestamp = null;
        switch (stageId) {
            case 'moc_created': matchedTimestamp = dispatchData.createdAt; break;
            case 'moc_approved_finance': matchedTimestamp = dispatchData.financeApprovedAt; break;
            case 'packing_scheduled': matchedTimestamp = dispatchData.packingScheduledAt; break;
            case 'packed_ready': matchedTimestamp = dispatchData.packedAt; break;
            case 'dc_created': matchedTimestamp = dispatchData.dcCreatedAt; break;
            case 'documents_ready': matchedTimestamp = dispatchData.documentsReadyAt; break;
            case 'qc_dispatched': matchedTimestamp = dispatchData.dispatchedAt; break;
            case 'delivered': matchedTimestamp = dispatchData.deliveredAt; break;
            default: break;
        }
        return matchedTimestamp ? (matchedTimestamp.toDate ? matchedTimestamp.toDate() : new Date(matchedTimestamp)) : null;
    }

    // Aggregate logic for Master Timeline
    if (!dispatchesData || dispatchesData.length === 0) return null;

    let earliestMs = null;
    for (const dispatch of dispatchesData) {
        let matchedTimestamp = null;
        switch (stageId) {
            case 'moc_created': matchedTimestamp = dispatch.createdAt; break;
            case 'moc_approved_finance': matchedTimestamp = dispatch.financeApprovedAt; break;
            case 'packing_scheduled': matchedTimestamp = dispatch.packingScheduledAt; break;
            case 'packed_ready': matchedTimestamp = dispatch.packedAt; break;
            case 'dc_created': matchedTimestamp = dispatch.dcCreatedAt; break;
            case 'documents_ready': matchedTimestamp = dispatch.documentsReadyAt; break;
            case 'qc_dispatched': matchedTimestamp = dispatch.dispatchedAt; break;
            case 'delivered': matchedTimestamp = dispatch.deliveredAt; break;
            default: break;
        }

        if (matchedTimestamp) {
            const currentMs = matchedTimestamp.toDate ? matchedTimestamp.toDate().getTime() : new Date(matchedTimestamp).getTime();
            if (!isNaN(currentMs)) {
                if (earliestMs === null || currentMs < earliestMs) {
                    earliestMs = currentMs;
                }
            }
        }
    }
    return earliestMs ? new Date(earliestMs) : null;
  };

  const formatTimestamp = (timestamp) => {
      if (!timestamp) return '';
      let date;
      if (timestamp instanceof Date) {
          date = timestamp;
      } else if (timestamp.toDate) {
          date = timestamp.toDate();
      } else if (typeof timestamp === 'string' || typeof timestamp === 'number') {
          date = new Date(timestamp);
      } else {
          return '';
      }
      
      if (isNaN(date.getTime())) return '';

      return date.toLocaleDateString('en-IN', { 
          day: '2-digit', 
          month: 'short', 
          hour: '2-digit', 
          minute: '2-digit', 
          hour12: true 
      });
  };

  return (
    <div style={{ marginBottom: '30px' }}>
      {title && (
        <h4 style={{ 
          fontSize: '13px', 
          fontWeight: '700', 
          color: '#4b5563', 
          marginBottom: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          textTransform: 'uppercase',
          letterSpacing: '0.025em'
        }}>
          {dispatchData ? <Package size={14} color="#6b7280" /> : <Clock size={14} color="#2563eb" />}
          {title}
        </h4>
      )}
        <div className="timeline-scroll-container" style={timelineContainerStyle}>
        <div style={timelineLineStyle} />
        {timelineStages.map((stage, index) => {
          const isCompleted = index < currentStageIndex;
          const isActive = index === currentStageIndex;
          const isFuture = index > currentStageIndex;
          const timestamp = getTimestampForStage(stage.id);

          return (
            <div key={stage.id} style={timelineItemStyle}>
              <div style={timelineNodeStyle(isCompleted, isActive, isFuture)}>
                {isCompleted ? <CheckCircle size={18} color="white" /> : stage.icon}
                {isActive && <div className="pulse-ring" style={pulseRingStyle} />}
              </div>
              <div style={timelineContentStyle(isCompleted, isActive)}>
                <span style={timelineLabelStyle(isActive)}>{stage.label}</span>
                {(isCompleted || (isActive && timestamp)) && (
                  <span style={timelineDateStyle}>{formatTimestamp(timestamp)}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ---------------------------
// Pickup (PC/MIC) Timeline
// ---------------------------
const PICKUP_STAGES = [
  { id: 'pickup_intimation', label: 'Pickup Intimation', icon: <Clock size={18} /> },
  { id: 'pc_ready', label: 'Pickup Challan Ready', icon: <ClipboardList size={18} /> },
  { id: 'pickup_complete', label: 'Pickup Complete', icon: <CheckCircle size={18} /> },
  { id: 'mic_complete', label: 'MIC Complete', icon: <ShieldCheck size={18} /> },
  { id: 'material_received', label: 'Material Received', icon: <Warehouse size={18} /> },
];

const getPickupStageFromData = (pc, mic, qc) => {
  if (!pc) return 'pickup_intimation';

  // Order of checks is reverse (Latest status wins)
  const micStatus = qc?.micStatus || mic?.micStatus || pc.micStatus || pc.status;
  
  if (micStatus === 'received') return 'material_received';
  if (micStatus === 'qc_passed') return 'mic_complete';
  if (micStatus === 'pending_qc' || micStatus === 'mic_ready') return 'pickup_complete';
  if (micStatus === 'mic_draft') return 'pickup_complete';
  if (pc.status === 'pc_ready' || pc.status === 'pc_completed') return 'pc_ready';

  return 'pickup_intimation';
};

const parseAnyTimestamp = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (value.toDate) return value.toDate();
  if (typeof value === 'number') {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === 'string') {
    // Support YYYY-MM-DD or ISO strings
    const d = new Date(value);
    if (!isNaN(d.getTime())) return d;
    return null;
  }
  return null;
};

const formatTimestampCompact = (timestamp) => {
  const d = parseAnyTimestamp(timestamp);
  if (!d) return '';
  return d.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

export const PickupTimeline = ({ pcData, pcsData, micData, micsByPcId, activity, title = 'Pickup Activity' }) => {
  // Support both old props (for backward compat) and new activity prop
  const pc = activity?.pc || pcData;
  const mic = activity?.mic || micData;
  const qc = activity?.qc;

  if (!pc && (!pcsData || pcsData.length === 0)) return null;

  let currentOverallStageId = 'pickup_intimation';
  
  if (pc) {
    // Single Lineage Timeline
    currentOverallStageId = getPickupStageFromData(pc); // Pass only the single document
  } else if (pcsData && pcsData.length > 0) {
    // Aggregate Master Pickup Timeline
    let furthestIndex = -1;
    for (const p of pcsData) {
      const m = micsByPcId ? micsByPcId[p.id] : null;
      const stageId = getPickupStageFromData(p); // Pass only the single document
      const stageIndex = PICKUP_STAGES.findIndex(s => s.id === stageId);
      if (stageIndex > furthestIndex) {
        furthestIndex = stageIndex;
        currentOverallStageId = stageId;
      }
    }
  }

  let currentStageIndex = PICKUP_STAGES.findIndex(s => s.id === currentOverallStageId);

  // Mark final stage as complete if truly received
  if (currentOverallStageId === 'material_received') {
    const finalStatus = qc?.micStatus || mic?.micStatus || pc?.status;
    if (finalStatus === 'received') currentStageIndex = PICKUP_STAGES.length;
  }

  const getTimestampForStage = (stageId) => {
    const getSingleLineageTimestamp = (p, m, q, sId) => {
        switch (sId) {
            case 'pickup_intimation':
              if (p.status === 'pc_draft') return null;
              return parseAnyTimestamp(p.intimatedAt) || parseAnyTimestamp(p.intimationDate) || parseAnyTimestamp(p.createdAt);
            case 'pc_ready':
              return parseAnyTimestamp(p.pcReadyAt) || parseAnyTimestamp(p.readyAt) || (['pc_ready', 'pc_completed', 'mic_draft', 'mic_ready'].includes(p.status) ? parseAnyTimestamp(p.updatedAt) : null);
            case 'pickup_complete':
              return parseAnyTimestamp(p.completedAt) || parseAnyTimestamp(m?.createdAt) || (['pc_completed', 'mic_draft', 'mic_ready'].includes(p.status) ? parseAnyTimestamp(p.updatedAt) : null);
            case 'mic_complete':
              return parseAnyTimestamp(q?.qcPassedAt) || parseAnyTimestamp(m?.qcPassedAt) || parseAnyTimestamp(m?.micCompletedAt) || parseAnyTimestamp(m?.updatedAt);
            case 'material_received':
              return parseAnyTimestamp(q?.receivedAt) || parseAnyTimestamp(m?.receivedAt) || parseAnyTimestamp(m?.receivedDate) || parseAnyTimestamp(p.receivedAt);
            default:
              return null;
        }
    };

    if (pc) {
        return getSingleLineageTimestamp(pc, mic, qc, stageId);
    }

    if (!pcsData || pcsData.length === 0) return null;

    let earliestMs = null;
    for (const p of pcsData) {
        const m = micsByPcId ? micsByPcId[p.id] : null;
        const ts = getSingleLineageTimestamp(p, m, null, stageId);
        if (ts) {
            const ms = ts.getTime();
            if (earliestMs === null || ms < earliestMs) {
                earliestMs = ms;
            }
        }
    }
    return earliestMs ? new Date(earliestMs) : null;
  };

  return (
    <div style={{ marginBottom: '30px' }}>
      <h4 style={{
        fontSize: '13px',
        fontWeight: '700',
        color: '#4b5563',
        marginBottom: '12px',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        textTransform: 'uppercase',
        letterSpacing: '0.025em'
      }}>
        <Truck size={14} color="#2563eb" />
        {title}
      </h4>
      <div className="timeline-scroll-container" style={timelineContainerStyle}>
        <div style={timelineLineStyle} />
        {PICKUP_STAGES.map((stage, index) => {
          const isCompleted = index < currentStageIndex;
          const isActive = index === currentStageIndex;
          const isFuture = index > currentStageIndex;
          const timestamp = getTimestampForStage(stage.id);

          return (
            <div key={stage.id} style={timelineItemStyle}>
              <div style={timelineNodeStyle(isCompleted, isActive, isFuture)}>
                {isCompleted ? <CheckCircle size={18} color="white" /> : stage.icon}
                {isActive && <div className="pulse-ring" style={pulseRingStyle} />}
              </div>
              <div style={timelineContentStyle(isCompleted, isActive)}>
                <span style={timelineLabelStyle(isActive)}>{stage.label}</span>
                {(isCompleted || (isActive && timestamp)) && (
                  <span style={timelineDateStyle}>{formatTimestampCompact(timestamp)}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// --- STYLES ---
const timelineContainerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  padding: '30px 20px',
  backgroundColor: 'white',
  borderRadius: '12px',
  overflowX: 'auto',
  minWidth: '100%',
  border: '1px solid #e5e7eb',
  gap: '5px',
  position: 'relative',
  boxSizing: 'border-box',
};

const timelineLineStyle = {
  position: 'absolute',
  top: '48px',
  left: '60px',
  right: '60px',
  height: '2px',
  backgroundColor: '#e5e7eb',
  zIndex: 0,
};

const timelineItemStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  position: 'relative',
  flex: '1 1 0',
  minWidth: '110px',
  textAlign: 'center',
  zIndex: 1,
};

const timelineNodeStyle = (isCompleted, isActive, isFuture) => ({
  width: '36px',
  height: '36px',
  borderRadius: '50%',
  backgroundColor: isCompleted ? '#10b981' : (isActive ? '#2563eb' : '#f3f4f6'),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: isCompleted || isActive ? 'white' : '#9ca3af',
  marginBottom: '12px',
  position: 'relative',
  border: isActive ? '2px solid white' : (isFuture ? '2px solid #e5e7eb' : 'none'),
  boxShadow: isActive ? '0 0 0 2px #2563eb' : 'none',
  transition: 'all 0.3s ease',
  opacity: isFuture ? 0.6 : 1,
  filter: isFuture ? 'grayscale(40%)' : 'none',
});

const pulseRingStyle = {
  position: 'absolute',
  width: '100%',
  height: '100%',
  borderRadius: '50%',
  border: '4px solid #2563eb',
  animation: 'pulse 2s infinite',
  boxSizing: 'border-box',
  pointerEvents: 'none',
};

const timelineContentStyle = (isCompleted, isActive) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '4px',
});

const timelineLabelStyle = (isActive) => ({
  fontSize: '11px',
  fontWeight: isActive ? '700' : '600',
  color: isActive ? '#1e293b' : '#64748b',
  lineHeight: '1.2',
  maxWidth: '90px',
});

const timelineDateStyle = {
  fontSize: '9px',
  color: '#94a3b8',
  fontWeight: '500',
};

const keyframes = `
  @keyframes pulse {
    0% { transform: scale(1); opacity: 0.6; }
    70% { transform: scale(1.5); opacity: 0; }
    100% { transform: scale(1); opacity: 0; }
  }
  .timeline-scroll-container::-webkit-scrollbar {
    display: none;
  }
  .timeline-scroll-container {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
`;

if (typeof document !== 'undefined') {
  const styleId = 'timeline-animations';
  if (!document.getElementById(styleId)) {
    const styleSheet = document.createElement('style');
    styleSheet.id = styleId;
    styleSheet.type = 'text/css';
    styleSheet.innerText = keyframes;
    document.head.appendChild(styleSheet);
  }
}

export default HorizontalTimeline;
